/**
 * An entity reference in agent prose, rendered as an inline citation.
 *
 * The link text is the entity's name; hovering shows a card with the kind, the
 * status snapshot the backend put in the link title, and an Open action.
 * Clicking navigates to the entity. Inside the Case IDE for the same case, a
 * draft / file / form / packet / plan opens in the editor pane instead of
 * reloading the workspace; inside an agent workspace, anything the artifact
 * panel can show opens there (the card still offers "Open in app").
 */
import { ArrowUpRight, PanelRight } from "lucide-react";
import { useCallback, type FC, type MouseEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationText
} from "@/components/ai-elements/inline-citation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCardTrigger } from "@/components/ui/hover-card";
import { useCaseIdeOptional } from "@/features/caseIde/context/CaseIdeContext";
import type { ArtifactKind } from "@/features/caseIde/types";
import { cn } from "@/lib/utils";
import {
  targetFromRef,
  useArtifactOpener
} from "../artifacts/ArtifactOpenerContext";

import {
  kindLabel,
  splitRefTitle,
  type EntityKind,
  type EntityRefTarget
} from "./entityRefs";

const IDE_KINDS: ReadonlySet<EntityKind> = new Set([
  "draft",
  "file",
  "form",
  "packet",
  "plan"
]);

const textOf = (node: ReactNode): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (typeof node === "object" && "props" in node) {
    return textOf((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
};

type Props = {
  target: EntityRefTarget;
  /** The markdown link title: `Kind · status · …`, a snapshot at write time. */
  title?: string | null;
  className?: string;
  children: ReactNode;
};

export const EntityRef: FC<Props> = ({
  target,
  title,
  className,
  children
}) => {
  const navigate = useNavigate();
  const ide = useCaseIdeOptional();
  const opener = useArtifactOpener();
  const label = textOf(children).trim();
  const parsed = splitRefTitle(title);
  const kind = parsed.kind ?? kindLabel(target);
  const to = target.path;

  // Same case, artifact kind: open in the editor pane rather than navigating.
  const opensInIde =
    ide !== null &&
    IDE_KINDS.has(target.kind) &&
    target.id !== undefined &&
    target.individualId === ide.individualId;
  // Inside a workspace with a viewer for this kind: show it in the panel.
  const opensInPanel =
    !opensInIde && opener !== null && opener.canOpen(target.kind);

  const navigateTo = useCallback(
    (event?: MouseEvent) => {
      event?.preventDefault();
      navigate(to);
    },
    [navigate, to]
  );

  const open = useCallback(
    (event?: MouseEvent) => {
      event?.preventDefault();
      if (opensInIde && ide && target.id) {
        ide.open({
          kind: target.kind as ArtifactKind,
          id: target.id,
          title: label || kindLabel(target)
        });
        return;
      }
      if (opensInPanel && opener) {
        opener.open(targetFromRef(target, label, title));
        return;
      }
      navigate(to);
    },
    [ide, label, navigate, opener, opensInIde, opensInPanel, target, title, to]
  );

  return (
    <InlineCitation
      data-testid="entity-ref"
      data-kind={target.kind}
      data-entity-id={target.id}
      className={className}
    >
      <InlineCitationCard openDelay={150} closeDelay={80}>
        <HoverCardTrigger asChild>
          <a
            href={to}
            onClick={open}
            className={cn(
              "inline rounded-sm border-b border-line-1 font-medium text-foreground no-underline",
              "hover:border-line-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            )}
          >
            <InlineCitationText>{children}</InlineCitationText>
          </a>
        </HoverCardTrigger>
        <InlineCitationCardBody
          className="w-72 p-3"
          data-testid="entity-ref-card"
        >
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="shrink-0 text-xs">
              {kind}
            </Badge>
            {parsed.detail && target.kind !== "rule" ? (
              <span className="truncate text-xs text-muted-foreground">
                {parsed.detail}
              </span>
            ) : null}
          </div>
          <div className="mt-2 text-sm leading-snug font-medium">
            {label || kindLabel(target)}
          </div>
          {parsed.detail && target.kind === "rule" ? (
            <p
              className="mt-1 text-xs leading-snug text-muted-foreground"
              data-testid="entity-ref-rule-text"
            >
              {parsed.detail}
            </p>
          ) : null}
          <div className="mt-3 flex gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={() => open()}
              data-testid="entity-ref-open"
            >
              {opensInIde
                ? "Open in editor"
                : opensInPanel
                ? "Preview"
                : `Open ${kind.toLowerCase()}`}
              {opensInPanel ? (
                <PanelRight className="size-3" />
              ) : (
                <ArrowUpRight className="size-3" />
              )}
            </Button>
            {opensInPanel ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs"
                onClick={() => navigateTo()}
                data-testid="entity-ref-open-app"
              >
                Open in app <ArrowUpRight className="size-3" />
              </Button>
            ) : null}
          </div>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
};
